/**
 * Encargos en Compras — la lógica, sin React.
 *
 * Una factura puede traer lo que se vendió a pedido mezclado con lo que se
 * compró para stock. Por cada línea con encargos abiertos del mismo producto
 * se proponen los más viejos, hasta las unidades que llegaron: es lo que el
 * cliente viene esperando hace más tiempo. El que carga lo confirma o lo
 * cambia antes de aplicar.
 */
import { aNumero, type Encargo } from "@/lib/tipos";

import { type FilaCompra } from "@/components/compras/logica";

/** Encargos tildados, por línea de la compra. */
export type SeleccionEncargos = Record<number, ReadonlyArray<number>>;

/** Las unidades que entran por esa línea. */
export function unidadesDeFila(fila: FilaCompra): number {
  return Math.floor(aNumero(fila.cantidad_recibida ?? fila.cantidad));
}

/** Las líneas que pueden cerrar encargos, con los encargos de su producto. */
export function filasConEncargos(
  filas: ReadonlyArray<FilaCompra>,
  encargos: ReadonlyArray<Encargo>,
): Array<{ fila: FilaCompra; encargos: Encargo[] }> {
  return filas
    .filter((f) => f.accion === "INGRESAR" && f.producto_id !== null)
    .map((fila) => ({
      fila,
      encargos: encargos
        .filter((e) => e.estado === "POR_RECIBIR" && e.producto_id === fila.producto_id)
        .sort((a, b) => a.fecha_venta.localeCompare(b.fecha_venta) || a.id - b.id),
    }))
    .filter((x) => x.encargos.length > 0);
}

/**
 * Lo que se propone tildar: los más viejos primero, sin pasarse de las
 * unidades de la línea, y sin usar el mismo encargo en dos líneas.
 */
export function propuestaDeEncargos(
  filas: ReadonlyArray<FilaCompra>,
  encargos: ReadonlyArray<Encargo>,
): SeleccionEncargos {
  const usados = new Set<number>();
  const seleccion: Record<number, number[]> = {};
  for (const { fila, encargos: delProducto } of filasConEncargos(filas, encargos)) {
    const elegidos = delProducto
      .filter((e) => !usados.has(e.id))
      .slice(0, unidadesDeFila(fila))
      .map((e) => e.id);
    elegidos.forEach((id) => usados.add(id));
    seleccion[fila.id] = elegidos;
  }
  return seleccion;
}

/** Tildar o destildar, sin pasarse de las unidades ni repetir un encargo. */
export function alternar(
  seleccion: SeleccionEncargos,
  fila: FilaCompra,
  encargoId: number,
): SeleccionEncargos {
  const actuales = seleccion[fila.id] ?? [];
  if (actuales.includes(encargoId)) {
    return { ...seleccion, [fila.id]: actuales.filter((id) => id !== encargoId) };
  }
  const enOtra = Object.entries(seleccion).some(
    ([f, ids]) => Number(f) !== fila.id && ids.includes(encargoId),
  );
  if (enOtra || actuales.length >= unidadesDeFila(fila)) return seleccion;
  return { ...seleccion, [fila.id]: [...actuales, encargoId] };
}

/** Lo que viaja al aplicar. */
export function asignaciones(
  seleccion: SeleccionEncargos,
): Array<{ encargo_id: number; fila_id: number }> {
  return Object.entries(seleccion).flatMap(([fila, ids]) =>
    ids.map((encargo_id) => ({ encargo_id, fila_id: Number(fila) })),
  );
}

/** «1 cierra encargos, 2 entran al stock». */
export function resumenDeFila(fila: FilaCompra, tildados: number): string {
  const total = unidadesDeFila(fila);
  const alStock = total - tildados;
  const partes: string[] = [];
  if (tildados > 0)
    partes.push(`${tildados} ${tildados === 1 ? "cierra un encargo" : "cierran encargos"}`);
  if (alStock > 0) partes.push(`${alStock} ${alStock === 1 ? "entra" : "entran"} al stock`);
  return partes.join(", ");
}
